import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import useApiRequest from 'ui/utilities/useApiRequest'
import { POTATO_TYPES } from 'helpers/constants'
import { showErrorToast } from 'ui/components/Toast/toast'
import { useProgram } from 'ui/hooks/useProgram'

const PotatoTypeContext = createContext()

export const usePotatoTypes = () => {
  const context = useContext(PotatoTypeContext)
  if (!context) {
    throw new Error('usePotatoTypes must be used within a PotatoTypeProvider')
  }
  return context
}

export function PotatoTypeProvider({ children }) {
  const { getData } = useApiRequest()
  const { authState } = useOktaAuth()
  const { generalSettings, potatoTypeHierarchy } = useProgram()
  const [potatoTypeMapping, setPotatoTypeMapping] = useState({})
  const [potatoTypes, setPotatoTypes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPotatoTypes = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await getData(POTATO_TYPES)
      if (response && Array.isArray(response)) {
        // Store the raw array
        setPotatoTypes(response)

        // Create mapping object for easy lookup
        const mapping = {}
        response.forEach((type) => {
          mapping[type.potatoTypeId] = type.description
        })
        setPotatoTypeMapping(mapping)
      }
    } catch (err) {
      setError(err)
      showErrorToast('Failed to load potato types', err)
    } finally {
      setIsLoading(false)
    }
  }, [getData])

  // Helper functions — memoized so the context value below (and anything downstream
  // that depends on these, e.g. net-new's pricing fetch pipeline) doesn't recompute
  // on every PotatoTypeProvider render when the underlying data hasn't actually changed.
  const getPotatoTypeById = useCallback(
    (id) => potatoTypeMapping[id] || null,
    [potatoTypeMapping]
  )

  const getPotatoTypeIdByName = useCallback(
    (name) =>
      Object.keys(potatoTypeMapping).find(
        (id) => potatoTypeMapping[id].toLowerCase() === name.toLowerCase()
      ),
    [potatoTypeMapping]
  )

  const getMinituberTypeId = useCallback(
    () => getPotatoTypeIdByName('minituber'),
    [getPotatoTypeIdByName]
  )

  const getSeedTypeId = useCallback(() => getPotatoTypeIdByName('seed'), [getPotatoTypeIdByName])

  const getChipstockTypeId = useCallback(
    () => getPotatoTypeIdByName('chipstock'),
    [getPotatoTypeIdByName]
  )

  const getPlantletTypeId = useCallback(
    () => getPotatoTypeIdByName('plantlet'),
    [getPotatoTypeIdByName]
  )

  // Given an input potato type ID, return the output potato type ID from the production hierarchy.
  // e.g. plantlet → minituber, minituber → seed, seed → chipstock
  const getOutputPotatoTypeId = useCallback(
    (inputPotatoTypeId) => {
      if (!inputPotatoTypeId || !Array.isArray(potatoTypeHierarchy)) return null
      const entry = potatoTypeHierarchy.find((pt) =>
        Array.isArray(pt.inputPotatoTypes) &&
        pt.inputPotatoTypes.some((inp) => String(inp.inputPotatoTypeId) === String(inputPotatoTypeId))
      )
      return entry?.outputPotatoTypeId ?? null
    },
    [potatoTypeHierarchy]
  )

  // Get UoM for a given potato type ID
  const getUomByPotatoTypeId = useCallback(
    (potatoTypeId) => {
      if (!generalSettings || !potatoTypeId) return null

      const potatoTypeName = getPotatoTypeById(potatoTypeId)
      if (!potatoTypeName) return null

      const normalizedName = potatoTypeName.toLowerCase()

      // Map potato type names to their corresponding UoM settings in general settings
      if (normalizedName === 'seed') {
        return generalSettings.seedWeightSettings?.uomCode || null
      }
      if (normalizedName === 'chipstock') {
        return generalSettings.chipStockWeightSettings?.uomCode || null
      }
      if (normalizedName === 'minituber') {
        return generalSettings.minituberWeightSettings?.uomCode || null
      }
      if (normalizedName === 'plantlet') {
        return generalSettings.plantletWeightSettings?.uomCode || null
      }

      return null
    },
    [generalSettings, getPotatoTypeById]
  )

  useEffect(() => {
    // Only attempt to fetch when authenticated
    if (authState?.isAuthenticated) {
      fetchPotatoTypes()
    } else {
      // Not authenticated yet, set loading to false
      setIsLoading(false)
    }
  }, [authState?.isAuthenticated])

  const value = useMemo(
    () => ({
      potatoTypes,
      potatoTypeMapping,
      isLoading,
      error,
      refetch: fetchPotatoTypes,
      // Helper functions
      getPotatoTypeById,
      getPotatoTypeIdByName,
      getMinituberTypeId,
      getSeedTypeId,
      getChipstockTypeId,
      getPlantletTypeId,
      getUomByPotatoTypeId,
      getOutputPotatoTypeId,
    }),
    [
      potatoTypes,
      potatoTypeMapping,
      isLoading,
      error,
      fetchPotatoTypes,
      getPotatoTypeById,
      getPotatoTypeIdByName,
      getMinituberTypeId,
      getSeedTypeId,
      getChipstockTypeId,
      getPlantletTypeId,
      getUomByPotatoTypeId,
      getOutputPotatoTypeId,
      generalSettings,
      potatoTypeHierarchy,
    ]
  )

  return <PotatoTypeContext.Provider value={value}>{children}</PotatoTypeContext.Provider>
}
